/**
 * Shared form utility functions for date/number parsing and formatting.
 */

import { isValid, parse } from 'date-fns'

/** Parses a "YYYY-MM-DD" string to a Date, returning undefined if empty or invalid. */
export function parseDateOnly(value: string | null | undefined): Date | undefined {
  if (!value) return undefined
  const date = parse(value, 'yyyy-MM-dd', new Date())
  return isValid(date) ? date : undefined
}

/** Returns true when both dates are set and the start date is after the end date. */
export function isDateOnlyRangeInverted(
  from: string | null | undefined,
  to: string | null | undefined,
): boolean {
  const fromDate = parseDateOnly(from)
  const toDate = parseDateOnly(to)
  if (!fromDate || !toDate) return false
  return fromDate > toDate
}

/** Converts a "YYYY-MM-DDTHH:mm" string to ISO 8601. */
export function toIso(val: string | null): string | null {
  if (!val) return null
  return val.includes(':00Z') || val.endsWith('Z') ? val : `${val}:00.000Z`
}

/** Parses a string to a float, returning null for empty/invalid values. */
export function parseOptionalFloat(val: string): number | null {
  if (!val.trim()) return null
  const n = parseFloat(val)
  return Number.isNaN(n) ? null : n
}

/** Parses a string to an integer, returning null for empty/invalid values. */
export function parseOptionalInt(val: string): number | null {
  if (!val.trim()) return null
  const n = parseInt(val, 10)
  return Number.isNaN(n) ? null : n
}

/** Converts a Date or ISO string to "YYYY-MM-DDTHH:mm" format for datetime-local inputs. */
export function toDateTimeLocal(value: string | Date | null): string {
  if (!value) return ''
  const date = typeof value === 'string' ? new Date(value) : value
  if (Number.isNaN(date.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** Converts a Date or ISO string to "YYYY-MM-DD" format for date-only inputs. */
export function toDateOnly(value: string | Date | null): string {
  if (!value) return ''
  const date = typeof value === 'string' ? new Date(value) : value
  if (Number.isNaN(date.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/** Converts a "YYYY-MM-DD" string to ISO 8601 midnight UTC string for APIs requiring datetime. */
export function dateOnlyToIso(val: string | null): string | null {
  if (!val) return null
  return `${val}T00:00:00.000Z`
}

/** Uppercases the leading country-code letters in CUI/VAT inputs (e.g. "ro12345678" → "RO12345678"). */
export function formatTaxIdInput(value: string): string {
  return value.replace(/^([A-Za-z]+)/, (prefix) => prefix.toUpperCase())
}
